import { conform, useForm } from '@conform-to/react'
import { getFieldsetConstraint, parse } from '@conform-to/zod'
import {
	json,
	redirect,
	type ActionFunctionArgs,
	type LoaderFunctionArgs,
	type MetaFunction,
} from '@remix-run/node'
import {
	Form,
	useActionData,
	useLoaderData,
	useSearchParams,
} from '@remix-run/react'
import { type TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'
import { AuthenticityTokenInput } from 'remix-utils/csrf/react'
import { HoneypotInputs } from 'remix-utils/honeypot/react'
import { safeRedirect } from 'remix-utils/safe-redirect'
import { z } from 'zod'
import { CheckboxField, ErrorList, Field } from '#app/components/forms.tsx'
import { Spacer } from '#app/components/spacer.tsx'
import { StatusButton } from '#app/components/ui/status-button.tsx'
import {
	requireAnonymous,
	sessionKey,
	signup,
	getUserId,
} from '#app/utils/auth.server.ts'
import { validateCSRF } from '#app/utils/csrf.server.ts'
import { prisma } from '#app/utils/db.server.ts'
import { checkHoneypot } from '#app/utils/honeypot.server.ts'
import { invariant, useIsPending } from '#app/utils/misc.tsx'
import { mixpanel } from '#app/utils/mixpanel.server.ts'
import { authSessionStorage } from '#app/utils/session.server.ts'
import { redirectWithToast } from '#app/utils/toast.server.ts'

import {
	NameSchema,
	PasswordAndConfirmPasswordSchema,
	UsernameSchema,
	type EmailSchemaTypes,
	type NameSchemaTypes,
	type PasswordAndConfirmPasswordSchemaTypes,
	type UsernameSchemaTypes,
	type PasswordSchemaTypes,
} from '#app/utils/user-validation.ts'
import { verifySessionStorage } from '#app/utils/verification.server.ts'
import {
	getLanguageIdFromPathname,
	getLanguageIdFromUrlString,
} from '#server/utils/getLanguageIdFromPathname.ts'

import { serverTranslations } from '#server/utils/serverTranslations.ts'
import { type VerifyFunctionArgs } from './verify.tsx'

const onboardingEmailSessionKey = 'onboardingEmail'

interface FormTranslations {
	username: UsernameSchemaTypes
	password: PasswordSchemaTypes
	name: NameSchemaTypes
	confirmPassword: PasswordAndConfirmPasswordSchemaTypes
	email: EmailSchemaTypes
	agreeToTermsOfServiceAndPrivacyPolicy: string
}

const SignupFormSchema = (translations: FormTranslations) => {
	return z
		.object({
			username: UsernameSchema(translations.username),
			name: NameSchema(translations.name),
			agreeToTermsOfServiceAndPrivacyPolicy: z.boolean({
				required_error: translations.agreeToTermsOfServiceAndPrivacyPolicy,
			}),
			remember: z.boolean().optional(),
			redirectTo: z.string().optional(),
		})
		.and(
			PasswordAndConfirmPasswordSchema(
				translations.password,
				translations.confirmPassword,
			),
		)
}

async function requireOnboardingEmail(request: Request) {
	await requireAnonymous(request)
	const verifySession = await verifySessionStorage.getSession(
		request.headers.get('cookie'),
	)
	const email = verifySession.get(onboardingEmailSessionKey)
	if (typeof email !== 'string' || !email) {
		throw redirect('/signup')
	}
	return email
}
export async function loader({ request }: LoaderFunctionArgs) {
	const email = await requireOnboardingEmail(request)
	const languageId = getLanguageIdFromPathname(request)
	const userId = await getUserId(request)

	mixpanel.track(`PageView: ${languageId} - Email Onboarding Landingpage`, {
		distinct_id: userId ?? 'anonymous',
		timeStamp: new Date().toISOString(),
	})

	return json({ email })
}

const formErrorsLoader = (t: TFunction): FormTranslations => {
	return {
		username: {
			required: t('usernameSchema.required'),
			minLength: t('usernameSchema.minLength'),
			maxLength: t('usernameSchema.maxLength'),
			pattern: t('usernameSchema.pattern'),
		},
		name: {
			required: t('nameSchema.required'),
			minLength: t('nameSchema.minLength'),
			maxLength: t('nameSchema.maxLength'),
		},
		email: {
			required: t('emailSchema.required'),
			invalid: t('emailSchema.invalid'),
			minLength: t('emailSchema.minLength'),
			maxLength: t('emailSchema.maxLength'),
		},
		password: {
			required: t('passwordSchema.required'),
			minLength: t('passwordSchema.minLength'),
			maxLength: t('passwordSchema.maxLength'),
		},
		confirmPassword: {
			matches: t('passwordConfirmSchema.matches'),
		},
		agreeToTermsOfServiceAndPrivacyPolicy: t(
			'agreeToTermsOfServiceAndPrivacyPolicy',
		),
	}
}

export async function action({ request }: ActionFunctionArgs) {
	const t = await serverTranslations(request, ['form', 'auth'])
	const languageId = getLanguageIdFromUrlString(request)
	const formErrors = formErrorsLoader(t)

	const email = await requireOnboardingEmail(request)
	const formData = await request.formData()
	await validateCSRF(formData, request.headers)
	checkHoneypot(formData)
	const submission = await parse(formData, {
		schema: intent =>
			SignupFormSchema(formErrors)
				.superRefine(async (data, ctx) => {
					const existingUser = await prisma.user.findUnique({
						where: { username: data.username },
						select: { id: true },
					})
					if (existingUser) {
						ctx.addIssue({
							path: ['username'],
							code: z.ZodIssueCode.custom,
							message: t('verification.userAlreadyExists', { ns: 'auth' }),
						})
						return
					}
				})
				.transform(async data => {
					if (intent !== 'submit') return { ...data, session: null }

					const session = await signup({ ...data, email, languageId })
					return { ...data, session }
				}),
		async: true,
	})

	if (submission.intent !== 'submit') {
		return json({ status: 'idle', submission } as const)
	}
	if (!submission.value?.session) {
		return json({ status: 'error', submission } as const, { status: 400 })
	}

	const { session, remember, redirectTo } = submission.value

	const authSession = await authSessionStorage.getSession(
		request.headers.get('cookie'),
	)
	authSession.set(sessionKey, session.id)
	const verifySession = await verifySessionStorage.getSession()
	const headers = new Headers()
	headers.append(
		'set-cookie',
		await authSessionStorage.commitSession(authSession, {
			expires: remember ? session.expirationDate : undefined,
		}),
	)
	headers.append(
		'set-cookie',
		await verifySessionStorage.destroySession(verifySession),
	)

	return redirectWithToast(
		safeRedirect(redirectTo),
		{
			title: t('onboarding.welcome', { ns: 'auth' }),
			description: t('onboarding.thanksForSigningUp', { ns: 'auth' }),
		},
		{ headers },
	)
}

export async function handleVerification({
	request,
	submission,
	currentLanguageId,
}: VerifyFunctionArgs) {
	invariant(submission.value, 'submission.value should be defined by now')
	const verifySession = await verifySessionStorage.getSession()
	verifySession.set(onboardingEmailSessionKey, submission.value.target)

	const url = new URL(request.url)
	const redirectTo = url.searchParams.get('redirectTo')

	let redirectUrl = `/${currentLanguageId}/onboarding`

	if (redirectTo) {
		redirectUrl += `?redirectTo=${encodeURIComponent(redirectTo)}`
	}

	return redirect(redirectUrl, {
		headers: {
			'set-cookie': await verifySessionStorage.commitSession(verifySession),
		},
	})
}

export const meta: MetaFunction = () => {
	return [{ title: 'Onboarding - Wiser' }]
}

export default function SignupRoute() {
	const data = useLoaderData<typeof loader>()
	const actionData = useActionData<typeof action>()
	let { t } = useTranslation(['form', 'auth'])
	const isPending = useIsPending()
	const [searchParams] = useSearchParams()
	const redirectTo = searchParams.get('redirectTo')

	const formErrors = formErrorsLoader(t)

	const [form, fields] = useForm({
		id: 'onboarding-form',
		constraint: getFieldsetConstraint(SignupFormSchema(formErrors)),
		defaultValue: { redirectTo },
		lastSubmission: actionData?.submission,
		onValidate({ formData }) {
			return parse(formData, { schema: SignupFormSchema(formErrors) })
		},
		shouldRevalidate: 'onBlur',
	})

	return (
		<div className="container flex h-screen flex-col justify-center pb-32 pt-20">
			<div className="mx-auto w-full max-w-lg">
				<div className="flex flex-col gap-3 text-center">
					<h1 className="text-[1.5rem] font-bold text-primary-400">
						{t('onboarding.welcomeAboard', { ns: 'auth' })} {data.email}!
					</h1>
					<p className="text-body-md text-muted-foreground">
						{t('onboarding.pleaseEnterDetails', { ns: 'auth' })}
					</p>
				</div>
				<Spacer size="xs" />
				<Form
					method="POST"
					className="mx-auto min-w-full max-w-sm sm:min-w-[468px]"
					{...form.props}
				>
					<AuthenticityTokenInput />
					<HoneypotInputs />
					<Field
						labelProps={{ htmlFor: fields.username.id }}
						inputProps={{
							...conform.input(fields.username),
							autoComplete: 'username',
							placeholder: t('onboarding.usernameToLogin', { ns: 'auth' }),
						}}
						errors={fields.username.errors}
					/>
					<Field
						labelProps={{ htmlFor: fields.name.id }}
						inputProps={{
							...conform.input(fields.name),
							autoComplete: 'name',
							placeholder: t('onboarding.name', { ns: 'auth' }),
						}}
						errors={fields.name.errors}
					/>
					<Field
						labelProps={{ htmlFor: fields.password.id }}
						inputProps={{
							...conform.input(fields.password, { type: 'password' }),
							autoComplete: 'new-password',
							placeholder: t('onboarding.password', { ns: 'auth' }),
						}}
						errors={fields.password.errors}
					/>

					<Field
						labelProps={{
							htmlFor: fields.confirmPassword.id,
						}}
						inputProps={{
							...conform.input(fields.confirmPassword, { type: 'password' }),
							autoComplete: 'new-password',
							placeholder: t('onboarding.confirmPassword', { ns: 'auth' }),
						}}
						errors={fields.confirmPassword.errors}
					/>

					<CheckboxField
						labelProps={{
							htmlFor: fields.agreeToTermsOfServiceAndPrivacyPolicy.id,
							children: t('onboarding.doYouAgreeWithTerms', { ns: 'auth' }),
						}}
						buttonProps={conform.input(
							fields.agreeToTermsOfServiceAndPrivacyPolicy,
							{ type: 'checkbox' },
						)}
						errors={fields.agreeToTermsOfServiceAndPrivacyPolicy.errors}
					/>
					<CheckboxField
						labelProps={{
							htmlFor: fields.remember.id,
							children: t('onboarding.rememberMe', { ns: 'auth' }),
						}}
						buttonProps={conform.input(fields.remember, { type: 'checkbox' })}
						errors={fields.remember.errors}
					/>

					<input {...conform.input(fields.redirectTo, { type: 'hidden' })} />
					<ErrorList errors={form.errors} id={form.errorId} />

					<div className="flex items-center justify-between gap-6">
						<StatusButton
							className="w-full"
							status={isPending ? 'pending' : actionData?.status ?? 'idle'}
							type="submit"
							disabled={isPending}
						>
							{t('onboarding.createMyAccount', { ns: 'auth' })}
						</StatusButton>
					</div>
				</Form>
			</div>
		</div>
	)
}
